import React, { useState } from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Divider, Grid, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowRight from '@mui/icons-material/ArrowRight';
import WarningIcon from '@mui/icons-material/Warning';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PsychologyIcon from '@mui/icons-material/Psychology';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import HealingIcon from '@mui/icons-material/Healing';
import NoteIcon from '@mui/icons-material/Note';
import DynamicInfographic from '../../components/infographics/DynamicInfographic';
import FallRiskInfographics from '../../components/infographics/FallRiskInfographics';
import ModuleSidebar from '../../components/navigation/ModuleSidebar';
import { scrollToTop } from '../../utils/scrollUtils';

const EmergencyResponse = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('4.1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [answerStatus, setAnswerStatus] = useState({}); // To track if answers are correct or incorrect
  
  const handleBack = () => {
    navigate('/courses/fall-prevention');
    scrollToTop();
  };

  // Function to navigate to the next section
  const handleNextSection = () => {
    const currentSectionIndex = sections.findIndex(section => section.id === activeSection);
    if (currentSectionIndex < sections.length - 1) {
      const nextSection = sections[currentSectionIndex + 1].id;
      setActiveSection(nextSection);
      scrollToTop();
    }
  };

  // Define correct answers for each question
  const correctAnswers = {
    '4.1-1': 2, // Do not move if serious injury suspected
    '4.1-2': 0, // Stay calm and reassure the person
    '4.1-3': 1, // Check for pain, bleeding, deformity
    '4.2-1': 0, // Loss of consciousness or confusion
    '4.2-2': 2, // Higher risk of internal bleeding
    '4.2-3': 1, // Some serious symptoms may be delayed
    '4.3-1': 2, // Lighting, floor conditions, and potential hazards
    '4.3-2': 0, // Date, time, and location of the fall
    '4.3-3': 1, // Update the care plan with new precautions
    '4.4-1': 1, // It can lead to activity restriction and social isolation
    '4.4-2': 2, // Tai Chi and other balance exercises
    '4.4-3': 1, // Using simple, consistent instructions with visual demonstrations
  };

  const handleAnswerSelect = (sectionId, questionId, answerIndex) => {
    const key = `${sectionId}-${questionId}`;
    
    // Check if the correct answer has already been selected for this question
    // If so, don't allow further selections
    if (answerStatus[key] === true) {
      return;
    }
    
    // Store the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [key]: answerIndex
    }));
    
    // Check if the selected answer is correct
    const isCorrect = correctAnswers[key] === answerIndex;
    
    // Only show explanation if the correct answer is selected
    if (isCorrect) {
      setShowExplanations(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If incorrect, ensure explanation is hidden
      setShowExplanations(prev => ({
        ...prev,
        [key]: false
      }));
    }
    
    // Track if answer is correct or incorrect
    setAnswerStatus(prev => ({
      ...prev,
      [key]: isCorrect
    }));
  };

  // Function to handle section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    scrollToTop();
  };

  // Define all sections for the module
  const sections = [
    {
      id: '4.1',
      title: '4.1 Immediate Actions After a Fall',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <LocalHospitalIcon sx={{ mr: 1, color: '#ba68c8' }} /> 4.1 Immediate Actions After a Fall
          </Typography>
          
          <Typography paragraph>
            The moments immediately following a fall are critical. Knowing how to respond quickly and appropriately 
            can prevent further injury and ensure the best possible outcome.
          </Typography>
          
          {/* Dynamic Infographic */}
          <DynamicInfographic module="EmergencyResponse" topic="Fall Emergency Response Protocol" />

          <Box sx={{ bgcolor: 'rgba(186, 104, 200, 0.1)', p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Emergency Response Protocol</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#ba68c8', fontWeight: 600 }}>
                      Step 1: Assess the Situation
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Stay calm and reassure the person" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Check for responsiveness" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Call for help if needed" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#ba68c8', fontWeight: 600 }}>
                      Step 2: Check for Injuries
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Look for obvious injuries" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Check for pain, bleeding, deformity" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Assess level of consciousness" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#ba68c8', fontWeight: 600 }}>
                      Step 3: Provide Assistance
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Do not move if serious injury suspected" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="If safe, help person to comfortable position" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Apply first aid as appropriate" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Quiz for Section 4.1 */}
          <Box sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ba68c8', borderBottom: '2px solid #ba68c8', pb: 1, display: 'inline-block' }}>
              Quick Knowledge Check
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Question 1 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the most important first step when you find someone who has fallen?
                    </Typography>
                    <List dense>
                      {[
                        "Call 911 immediately regardless of the situation",
                        "Help the person get up as quickly as possible",
                        "Stay calm and assess the situation before acting"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.1-1'] === index) {
                          if (index === correctAnswers['4.1-1']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.1-1'] && index === correctAnswers['4.1-1']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.1', '1', index)}
                            disabled={answerStatus['4.1-1'] === true && index !== correctAnswers['4.1-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.1-1'] === true && index !== correctAnswers['4.1-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.1-1'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Staying calm and assessing the situation is crucial before taking any action. This allows you to determine the severity of the fall and what kind of help is needed, preventing potential further injury from inappropriate responses.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Question 2 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      When checking for injuries after a fall, what should you look for?
                    </Typography>
                    <List dense>
                      {[
                        "Only visible bleeding",
                        "Pain, bleeding, deformity, and level of consciousness",
                        "Just ask the person if they are hurt"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.1-2'] === index) {
                          if (index === correctAnswers['4.1-2']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.1-2'] && index === correctAnswers['4.1-2']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.1', '2', index)}
                            disabled={answerStatus['4.1-2'] === true && index !== correctAnswers['4.1-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.1-2'] === true && index !== correctAnswers['4.1-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.1-2'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> A thorough assessment includes checking for pain, bleeding, visible deformities (which may indicate fractures), and the person's level of consciousness. This comprehensive check helps identify both obvious and less apparent injuries.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Question 3 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      When should you NOT attempt to move someone who has fallen?
                    </Typography>
                    <List dense>
                      {[
                        "When they request to stay where they are",
                        "When they are elderly, regardless of injuries",
                        "When you suspect a serious injury such as a head, neck, or back injury"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.1-3'] === index) {
                          if (index === correctAnswers['4.1-3']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.1-3'] && index === correctAnswers['4.1-3']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.1', '3', index)}
                            disabled={answerStatus['4.1-3'] === true && index !== correctAnswers['4.1-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.1-3'] === true && index !== correctAnswers['4.1-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.1-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          If you suspect a serious injury, especially to the head, neck, or back, do not move the person as this could cause further damage. Instead, keep them still, comfort them, and wait for professional medical help to arrive.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '4.2',
      title: '4.2 Recognizing When to Seek Medical Help',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <LocalHospitalIcon sx={{ mr: 1, color: '#ba68c8' }} /> 4.2 Recognizing When to Seek Medical Help
          </Typography>
          
          <Typography paragraph>
            Knowing when to seek medical attention after a fall can be lifesaving. Some serious injuries may not be 
            immediately apparent, making it crucial to recognize warning signs that require professional evaluation.
          </Typography>
          
          {/* Warning Signs Infographic */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                <Grid container>
                  <Grid item xs={12} md={5}>
                    <Box sx={{ height: 350, bgcolor: 'rgba(186, 104, 200, 0.15)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                      <WarningIcon sx={{ fontSize: 100, color: '#ba68c8', mb: 2 }} />
                      <Typography variant="h5" align="center" sx={{ color: '#ba68c8', fontWeight: 600 }}>
                        WARNING SIGNS
                      </Typography>
                      <Typography variant="subtitle1" align="center" sx={{ mt: 2 }}>
                        Know when to seek immediate medical attention after a fall
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="h6" gutterBottom>Warning Signs Requiring Medical Attention</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><WarningIcon sx={{ color: '#ba68c8' }} /></ListItemIcon>
                          <ListItemText primary="Loss of consciousness or confusion" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><WarningIcon sx={{ color: '#ba68c8' }} /></ListItemIcon>
                          <ListItemText primary="Severe pain or inability to bear weight" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><WarningIcon sx={{ color: '#ba68c8' }} /></ListItemIcon>
                          <ListItemText primary="Visible deformity or bone protrusion" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><WarningIcon sx={{ color: '#ba68c8' }} /></ListItemIcon>
                          <ListItemText primary="Bleeding that doesn't stop with pressure" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><WarningIcon sx={{ color: '#ba68c8' }} /></ListItemIcon>
                          <ListItemText primary="Head injury with vomiting or severe headache" />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
          
          {/* Quiz for Section 4.2 */}
          <Box sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ba68c8', borderBottom: '2px solid #ba68c8', pb: 1, display: 'inline-block' }}>
              Quick Knowledge Check
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Question 1 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following is considered a severe warning sign that requires immediate medical attention?
                    </Typography>
                    <List dense>
                      {[
                        "Loss of consciousness or confusion",
                        "Mild bruising on the arm",
                        "Slight discomfort when walking"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.2-1'] === index) {
                          if (index === correctAnswers['4.2-1']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.2-1'] && index === correctAnswers['4.2-1']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.2', '1', index)}
                            disabled={answerStatus['4.2-1'] === true && index !== correctAnswers['4.2-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.2-1'] === true && index !== correctAnswers['4.2-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.2-1'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Loss of consciousness or confusion after a fall is a severe warning sign that requires immediate medical attention as it may indicate a serious head injury or other critical conditions that need urgent evaluation and treatment.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Question 2 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why are falls particularly concerning for individuals taking anticoagulant medications?
                    </Typography>
                    <List dense>
                      {[
                        "They may experience more pain",
                        "They are more likely to have broken bones",
                        "They have a higher risk of internal bleeding"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.2-2'] === index) {
                          if (index === correctAnswers['4.2-2']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.2-2'] && index === correctAnswers['4.2-2']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.2', '2', index)}
                            disabled={answerStatus['4.2-2'] === true && index !== correctAnswers['4.2-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.2-2'] === true && index !== correctAnswers['4.2-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.2-2'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Individuals taking anticoagulant medications (blood thinners) have a higher risk of internal bleeding after a fall, even from minor trauma. This bleeding may not be immediately apparent but can be life-threatening, which is why medical evaluation is crucial even for seemingly minor falls in these individuals.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Question 3 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is an important consideration regarding symptoms after a fall?
                    </Typography>
                    <List dense>
                      {[
                        "All serious symptoms appear immediately after a fall",
                        "Some serious symptoms may be delayed and appear hours after a fall",
                        "Symptoms that appear the next day are never related to the fall"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.2-3'] === index) {
                          if (index === correctAnswers['4.2-3']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.2-3'] && index === correctAnswers['4.2-3']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.2', '3', index)}
                            disabled={answerStatus['4.2-3'] === true && index !== correctAnswers['4.2-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.2-3'] === true && index !== correctAnswers['4.2-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.2-3'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Some serious symptoms, such as internal bleeding or brain injuries, may not be immediately apparent and can develop hours after a fall. This is why continued monitoring after a fall is important, and any new or worsening symptoms should prompt medical evaluation, even if the initial assessment seemed fine.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '4.3',
      title: '4.3 Documenting and Reporting Falls',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <AssignmentIcon sx={{ mr: 1, color: '#ba68c8' }} /> 4.3 Documenting and Reporting Falls
          </Typography>
          
          <Typography paragraph>
            Proper documentation and reporting of falls is essential for identifying patterns, adjusting care plans, 
            and preventing future incidents.
          </Typography>
          
          {/* Documentation Form Infographic */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <Card sx={{ maxWidth: 800, width: '100%' }}>
              <Box sx={{ p: 4, bgcolor: 'rgba(186, 104, 200, 0.1)' }}>
                <Typography variant="h5" align="center" gutterBottom sx={{ color: '#ba68c8', fontWeight: 600 }}>
                  FALL INCIDENT REPORT
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Essential Information to Document:</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><NoteIcon sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Date, time, and location of fall" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><NoteIcon sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Activity at time of fall" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><NoteIcon sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Environmental factors (lighting, floor conditions)" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><NoteIcon sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Medications taken within 24 hours" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><NoteIcon sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Injuries sustained and treatment provided" />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Follow-up Actions:</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Notify healthcare provider" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Update care plan with new precautions" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Implement preventive measures" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Schedule follow-up assessment" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                        <ListItemText primary="Review medication regimen" />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Box>
          
          {/* Quiz for Section 4.3 */}
          <Box sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ba68c8', borderBottom: '2px solid #ba68c8', pb: 1, display: 'inline-block' }}>
              Quick Knowledge Check
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Question 1 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What environmental factors should be documented after a fall?
                    </Typography>
                    <List dense>
                      {[
                        "Only the room temperature",
                        "Just the time of day",
                        "Lighting, floor conditions, and potential hazards"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.3-1'] === index) {
                          if (index === correctAnswers['4.3-1']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.3-1'] && index === correctAnswers['4.3-1']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.3', '1', index)}
                            disabled={answerStatus['4.3-1'] === true && index !== correctAnswers['4.3-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.3-1'] === true && index !== correctAnswers['4.3-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.3-1'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Comprehensive documentation of environmental factors such as lighting, floor conditions (wet, slippery, uneven), and potential hazards (cords, rugs, clutter) is essential for identifying and addressing fall risks to prevent future incidents.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Question 2 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What basic information should always be included in a fall incident report?
                    </Typography>
                    <List dense>
                      {[
                        "Date, time, and location of the fall",
                        "Only the injuries sustained",
                        "Just the caregiver's name"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.3-2'] === index) {
                          if (index === correctAnswers['4.3-2']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.3-2'] && index === correctAnswers['4.3-2']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.3', '2', index)}
                            disabled={answerStatus['4.3-2'] === true && index !== correctAnswers['4.3-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.3-2'] === true && index !== correctAnswers['4.3-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.3-2'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> The date, time, and location of the fall are fundamental pieces of information that must be documented. This information helps identify patterns (such as falls occurring at certain times or in specific locations) and enables targeted preventive measures.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Question 3 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is an important follow-up action after documenting a fall?
                    </Typography>
                    <List dense>
                      {[
                        "File the report and take no further action",
                        "Update the care plan with new precautions",
                        "Wait for another fall to establish a pattern"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.3-3'] === index) {
                          if (index === correctAnswers['4.3-3']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.3-3'] && index === correctAnswers['4.3-3']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.3', '3', index)}
                            disabled={answerStatus['4.3-3'] === true && index !== correctAnswers['4.3-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.3-3'] === true && index !== correctAnswers['4.3-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.3-3'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> After documenting a fall, it's essential to update the care plan with new precautions based on what was learned from the incident. This proactive approach helps prevent future falls by addressing identified risks and implementing appropriate interventions tailored to the individual's specific needs.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '4.4',
      title: '4.4 Psychological and Physical Recovery Support',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <PsychologyIcon sx={{ mr: 1, color: '#ba68c8' }} /> 4.4 Psychological and Physical Recovery Support
          </Typography>
          
          <Typography paragraph>
            Recovery after a fall involves both physical rehabilitation and psychological support. Many older adults 
            develop a fear of falling that can significantly impact their quality of life and independence.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#ba68c8', fontWeight: 600 }}>
                    <PsychologyIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Addressing Fear of Falling
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Fear of falling can lead to activity restriction, decreased mobility, and social isolation, 
                    creating a cycle that actually increases fall risk.
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Acknowledge feelings" 
                        secondary="Validate concerns while encouraging positive thinking"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Gradual confidence building" 
                        secondary="Start with safe activities and gradually increase challenge"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Cognitive behavioral approaches" 
                        secondary="Challenge negative thoughts about falling"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Social support" 
                        secondary="Encourage participation in group activities"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#ba68c8', fontWeight: 600 }}>
                    <FitnessCenterIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Physical Rehabilitation
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Physical therapy and targeted exercises can help restore strength, balance, and mobility after a fall.
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Balance exercises" 
                        secondary="Tai Chi, standing on one foot, heel-to-toe walking"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Strength training" 
                        secondary="Focus on lower body and core muscles"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Gait training" 
                        secondary="Proper walking technique with assistive devices if needed"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#ba68c8' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Functional activities" 
                        secondary="Practice everyday movements like sitting to standing"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Condition-Specific Considerations */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(186, 104, 200, 0.05)', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Condition-Specific Recovery Considerations</Typography>
            <Typography variant="body2" paragraph>
              Recovery approaches should be tailored to the individual's specific health conditions:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ color: '#ba68c8', fontWeight: 600 }}>Parkinson's Disease:</Typography>
                <List dense disablePadding>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ArrowRight sx={{ color: '#ba68c8', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText primary="Focus on freezing of gait" secondary="Cueing strategies" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ArrowRight sx={{ color: '#ba68c8', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText primary="Medication timing" secondary="For optimal mobility" />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ color: '#ba68c8', fontWeight: 600 }}>Arthritis:</Typography>
                <List dense disablePadding>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ArrowRight sx={{ color: '#ba68c8', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText primary="Joint protection" secondary="During recovery exercises" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ArrowRight sx={{ color: '#ba68c8', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText primary="Pain management" secondary="Before rehabilitation sessions" />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ color: '#ba68c8', fontWeight: 600 }}>Dementia:</Typography>
                <List dense disablePadding>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ArrowRight sx={{ color: '#ba68c8', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText primary="Simple, consistent instructions" secondary="Visual demonstrations" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ArrowRight sx={{ color: '#ba68c8', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText primary="Familiar environments" secondary="For exercise sessions" />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" sx={{ color: '#ba68c8', fontWeight: 600 }}>Diabetes:</Typography>
                <List dense disablePadding>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ArrowRight sx={{ color: '#ba68c8', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText primary="Foot inspection" secondary="Before and after exercise" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ArrowRight sx={{ color: '#ba68c8', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText primary="Blood sugar monitoring" secondary="During recovery period" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Box>
          
          {/* Quiz for Section 4.4 */}
          <Box sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ba68c8', borderBottom: '2px solid #ba68c8', pb: 1, display: 'inline-block' }}>
              Quick Knowledge Check
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Question 1 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      How can fear of falling negatively impact an older adult?
                    </Typography>
                    <List dense>
                      {[
                        "It has no significant impact on daily life",
                        "It can lead to activity restriction and social isolation",
                        "It only affects those with previous injuries"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.4-1'] === index) {
                          if (index === correctAnswers['4.4-1']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.4-1'] && index === correctAnswers['4.4-1']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.4', '1', index)}
                            disabled={answerStatus['4.4-1'] === true && index !== correctAnswers['4.4-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.4-1'] === true && index !== correctAnswers['4.4-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.4-1'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Fear of falling can lead to activity restriction, decreased mobility, and social isolation. This creates a negative cycle that actually increases fall risk as muscles weaken from disuse and confidence decreases further.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Question 2 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which physical rehabilitation approach is particularly beneficial for improving balance after a fall?
                    </Typography>
                    <List dense>
                      {[
                        "Complete bed rest for several weeks",
                        "Focusing only on upper body strength",
                        "Tai Chi and other balance exercises"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.4-2'] === index) {
                          if (index === correctAnswers['4.4-2']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.4-2'] && index === correctAnswers['4.4-2']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.4', '2', index)}
                            disabled={answerStatus['4.4-2'] === true && index !== correctAnswers['4.4-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.4-2'] === true && index !== correctAnswers['4.4-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.4-2'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> Tai Chi and other balance exercises have been shown to be particularly effective for improving balance, reducing fear of falling, and preventing future falls. These exercises focus on controlled movements, weight shifting, and postural awareness.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Question 3 */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(186, 104, 200, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What special consideration should be made for recovery support in a person with dementia?
                    </Typography>
                    <List dense>
                      {[
                        "Providing complex written instructions",
                        "Using simple, consistent instructions with visual demonstrations",
                        "Avoiding all physical activity"
                      ].map((option, index) => {
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['4.4-3'] === index) {
                          if (index === correctAnswers['4.4-3']) {
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['4.4-3'] && index === correctAnswers['4.4-3']) {
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('4.4', '3', index)}
                            disabled={answerStatus['4.4-3'] === true && index !== correctAnswers['4.4-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['4.4-3'] === true && index !== correctAnswers['4.4-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['4.4-3'] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(186, 104, 200, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2">
                          <strong>Explanation:</strong> For individuals with dementia, using simple, consistent instructions with visual demonstrations is essential for effective recovery support. This approach accommodates cognitive limitations while still enabling participation in important rehabilitation activities.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    }
  ];
  
  // Find the active section content
  const activeSectionContent = sections.find(section => section.id === activeSection)?.content;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button 
        variant="outlined" 
        startIcon={<ArrowBackIcon />} 
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Learning Modules
      </Button>

      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ color: '#ba68c8', fontWeight: 600 }}>
          Module 4: Emergency Response and Post-Fall Care
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: 'text.secondary' }}>
          This module covers immediate actions after a fall, recognizing when to seek medical help, 
          proper documentation and reporting procedures, and psychological and physical recovery support.
        </Typography>

        <Divider sx={{ my: 3 }} />
        
        {/* Module Content with Sidebar */}
        <Grid container spacing={3}>
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <ModuleSidebar 
              sections={sections} 
              activeSection={activeSection} 
              onSectionChange={handleSectionChange} 
              moduleTitle="Emergency Response"
            />
          </Grid>
          
          {/* Content */}
          <Grid item xs={12} md={9}>
            {activeSectionContent}
            
            {/* Module Buttons */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="outlined" 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBack}
              >
                Back to Learning Modules
              </Button>
              {activeSection !== sections[sections.length - 1].id && (
                <Button 
                  variant="contained" 
                  endIcon={<ArrowForwardIcon />} 
                  onClick={handleNextSection}
                  sx={{ bgcolor: '#ba68c8', '&:hover': { bgcolor: '#9c27b0' } }}
                >
                  Next Section
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default EmergencyResponse;
